/* oxlint-disable typescript/no-explicit-any */
import { ComponentType, useCallback, useMemo } from 'react';

import { StaticPageNames } from '@/constants';
import { useLayoutDBSync, useUpdateFromDbAndGetStorageLayoutIds } from '@/hooks';
import { useLayoutStorage } from '@/stores';
import { GenericWidgetProps } from '@/types.ts';

import {
  areLayoutsEqual,
  getLayoutUpdateRequestFromSiteControlLayout,
} from './helpFunctions/index.ts';

export type WidgetDescriptor<
  Props extends GenericWidgetProps & { id: string; widgetId: string; config?: Config },
  Config,
> = {
  Component: ComponentType<Props>;
  defaultPropsConfig: Config;
};

type UseWidgetInfoProps<WDs extends readonly WidgetDescriptor<any, any>[]> = {
  widgets: WDs;
  pageName: StaticPageNames;
};

export function useWidgetInfo<WDs extends readonly WidgetDescriptor<any, any>[]>({
  widgets,
  pageName,
}: UseWidgetInfoProps<WDs>) {
  const { updateLayoutInDb, getLayoutById, source } = useLayoutDBSync();
  const findStoredLayoutById = useLayoutStorage(s => s.findStoredLayoutById);

  const widgetInfos = useUpdateFromDbAndGetStorageLayoutIds({
    pageName,
    source,
    widgets,
  });

  // These widgets share layoutId so we can just check the first one
  const layoutId = Object.values(widgetInfos)[0]?.layoutId;
  const storedLayout = layoutId ? findStoredLayoutById(layoutId) : undefined;

  const saveLayout = useCallback(async () => {
    if (storedLayout) {
      const layoutUpdateRequest = getLayoutUpdateRequestFromSiteControlLayout(storedLayout);
      await updateLayoutInDb(layoutUpdateRequest);
    }
  }, [updateLayoutInDb, storedLayout]);

  const isDirty = useMemo(() => {
    if (!layoutId) return false;
    const dbLayout = getLayoutById(layoutId);
    if (!storedLayout || !dbLayout) return false;
    return !areLayoutsEqual({ currentLayout: storedLayout, currentDbLayout: dbLayout });
  }, [storedLayout, getLayoutById, layoutId]);

  return { isDirty: isDirty || false, saveLayout, widgetInfos };
}
